<template>
  <div v-if="creator">
    <div
      class="hr-page__topmatter"
      :inert="anyMenusOpen"
    >
      <BackButton />

      <div class="hr-page__header__top">
        <div class="page-title">
          {{ `${creator.display_name}’s Hire Requests` }}
        </div>
        <CreatorHeader
          :value="creator"
          v-if="creator"
          @refresh="refreshAll"
        />
      </div>
    </div>
    <CreatorHireRequestsTable
      :creator="creator"
      :list="list"
      :class="{ 'table-loading': loadingList }"
      :listOptions="listOptions"
      @orderUpdate="orderUpdate($event)"
      @filterUpdate="filterUpdate($event)"
      :reloadKey="reloadKey"
      :inertProp="anyMenusOpen"
      @menuEvent="menus.table = $event"
    >
      <template #actions>
        <TableOptionsResetBtn
          :enabled="anyActiveListOptions"
          @input="resetListOptions"
          :inert="anyMenusOpen"
        />
        <div class="payments-table__action">
          <div class="payments-table__action__label">Filter by:</div>
          <v-overlay
            :value="menus.filters"
            @click.native.stop="closeFilters()"
          />
          <v-menu
            bottom
            offset-y
            :close-on-click="false"
            :close-on-content-click="false"
            v-model="menus.filters"
            light
            class="filters-menu"
            content-class="filters-menu__content"
          >
            <template v-slot:activator="{ on }">
              <TableActiveFilters
                v-on="on"
                @openMenu="menus.filters = !menus.filters"
                :listOptions="listOptions"
                :fields="filterFields"
                @resetFilter="resetFilter($event)"
                :activeListOption="activeListOption"
                ref="tableActiveFilters"
                :filtersMenu="menus.filters"
                :inert="anyMenusOpen && !menus.filters"
              />
            </template>
            <v-card
              class="filters-menu__content__card"
              :aria-modal="menus.filters"
              :aria-live="menus.filters ? 'polite' : false"
            >
              <div class="filters-menu__body">
                <v-expansion-panels
                  v-model="filterPanels"
                  multiple
                  flat
                >
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      Project
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <InternalProjectsSelector
                        v-model="listOptions.internal_project_id"
                        emptyLabel="All"
                        :params="{ hire_requests: true, user_id: creator.id }"
                        @input="
                          optionsUpdate();
                        closeFilters();
                        "
                        multiple
                        chipText="code"
                        @remove="
                          removeFromFilter('internal_project_id', $event);
                        optionsUpdate();
                        closeFilters();
                        "
                      />
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                  <TableOptionsFilterPanel
                    :listOptions="listOptions"
                    fieldName="contract_status"
                    @filterUpdate="filterMenuUpdate($event)"
                    fieldLabel="Contract Status"
                    selectAll
                    reset
                    :defaultListOptions="getDefaultListOptions()"
                  />
                  <TableDateFilterPanel
                    :listOptions="listOptions"
                    fieldName="created_at"
                    @filterUpdate="filterMenuUpdate($event)"
                    fieldLabel="Date Created"
                  />
                  <TableDateFilterPanel
                    :listOptions="listOptions"
                    fieldName="start_date"
                    @filterUpdate="filterMenuUpdate($event)"
                    fieldLabel="Start Date"
                  />
                  <TableDateFilterPanel
                    :listOptions="listOptions"
                    fieldName="end_date"
                    @filterUpdate="filterMenuUpdate($event)"
                    fieldLabel="End Date"
                  />
                  <v-expansion-panel>
                    <v-expansion-panel-header> Role </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <SkillsSelector
                        v-model="listOptions.skill_id"
                        placeholder="Role"
                        @input="
                          optionsUpdate();
                        closeFilters();
                        "
                      />
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </v-card>
          </v-menu>
        </div>
        <div
          class="payments-table__action"
          :inert="anyMenusOpen"
        >
          <div class="payments-table__action__label"></div>
          <TextField
            v-model="searchInputBuffer"
            emptyLabel="All"
            :params="{ hire_requests: true }"
            placeholder="Search"
            inputOnEnter
            @enter="searchInputEvent"
            ref="searchField"
          />
        </div>
        <div
          class="payments-table__secondary-actions"
          :inert="anyMenusOpen"
        >
          <TableOptionsResetBtn
            :enabled="anyActiveListOptions"
            @input="resetListOptions"
            mobile
          />
        </div>
      </template>
      <template #pagination>
        <TablePagination
          :page="page"
          :totalPages="totalPages"
          @input="page = $event"
          :key="`pagination-${reloadKey}`"
          :inert="anyMenusOpen"
        />
      </template>
    </CreatorHireRequestsTable>
  </div>
</template>

<script>
import IndexPage from "./index-page";
import CreatorHireRequestsTable from "./creator-hire-requests/table.creator-hire-requests";
import hireRequestsTableActions from "@/mixins/hire-requests/hire-requests-table-actions.mixin";
import paginatedTable from "@/mixins/paginated-table.mixin";
import TablePagination from "@/components/tables/TablePagination";
import TableDateFilterPanel from "@/components/tables/TableDateFilterPanel";
import TableOptionsFilterPanel from "@/components/tables/TableOptionsFilterPanel";
import TableActiveFilters from "@/components/tables/TableActiveFilters";
import CreatorHeader from "@/components/CreatorHeader";
import TableOptionsResetBtn from "@/components/tables/TableOptionsResetBtn";
import pageMenuEventBubbler from "@/mixins/menu-event-bubblers/meb-page.mixin.js";

export default {
  components: {
    CreatorHireRequestsTable,
    TablePagination,
    TableDateFilterPanel,
    TableOptionsFilterPanel,
    TableActiveFilters,
    CreatorHeader,
    TableOptionsResetBtn,
  },
  extends: IndexPage,
  mixins: [hireRequestsTableActions, paginatedTable, pageMenuEventBubbler],
  head() {
    return {
      title: "Creator Hire Requests",
      titleTemplate: "%s | iU Community",
    };
  },
  data() {
    return {
      creator: null,
      list: [],
      loadingList: false,
      reloadKey: 1,
      filterFields: [
        { name: "internal_project_id", label: "Project" },
        { name: "contract_status", label: "Status" },
        { name: "start_date", label: "Start Date" },
        { name: "end_date", label: "End Date" },
        { name: "created_at", label: "Date Requested" },
        { name: "skill_id", label: "Role" },
      ],
      menus: {
        filters: false,
        table: false,
      },
      searchInputBuffer: "",
    };
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    async refreshAll() {
      this.sendAgreementDialog = false;
      this.fetchList();
      this.reloadKey++;
    },
    getParams() {
      const params = { ...this.listParams };
      params.page = this.page;
      params.per = ["sm", "xs"].includes(this.$size) ? 5 : 10;
      params.creator_id = this.$route.params.id;
      return params;
    },
    async fetchList() {
      this.loadingList = true;
      const params = this.getParams();
      const { data } = await this.$api.HireRequest.creator(params);

      this.creator = data.creator;
      this.list = data.hire_requests;
      this.totalPages = data.total_pages;
      this.loadingList = false;
      if (this.focusOnSearch) {
        this.focusOnSearch = false;
        this.$nextTick(() => this.$refs.searchField.$refs.input.focus());
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/table-filters-menu.scss";

.table-loading {
  pointer-events: none;
  opacity: 0.5;
}

.hr-page__header__top {
  width: 100%;
  margin-bottom: 50px;
  .page-title {
    margin-bottom: 57px;
  }
  @media screen and (max-width: 960px) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 31px;
  }
}
</style>
